var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/** @jsx jsx */
import { React, jsx, css, Immutable, polished, getAppStore, classNames } from 'jimu-core';
import { Dropdown, DropdownMenu, DropdownItem, DropdownButton } from './dropdown';
import { Checkbox } from './switch';
import { withStyles } from 'jimu-theme';
const MULTI_SELECT_ITEM_CLASS_NAME = 'multi-select-item';
const DEFAULT_MAX_HEIGHT = 300;
/**
 * The `MultiSelect` component is used to select multiple options from a `DropdownMenu`.
 *
 * ```ts
 * import { MultiSelect } from 'jimu-ui'
 * ```
 */
export class _MultiSelect extends React.PureComponent {
    constructor(props) {
        super(props);
        this.getValueLabels = () => {
            const valueLabels = {};
            this.props.items.forEach(item => {
                valueLabels[item.value] = item.label;
            });
            return valueLabels;
        };
        this.onDropDownToggle = (evt) => {
            var _a;
            evt === null || evt === void 0 ? void 0 : evt.stopPropagation();
            const targetClass = (_a = evt === null || evt === void 0 ? void 0 : evt.currentTarget) === null || _a === void 0 ? void 0 : _a.className;
            if (targetClass && targetClass.indexOf(MULTI_SELECT_ITEM_CLASS_NAME) > -1) {
                return;
            }
            const { isOpen } = this.state;
            this.setState({ isOpen: !isOpen });
        };
        this.handleMenuClick = (evt) => {
            evt.stopPropagation();
            evt.nativeEvent.stopImmediatePropagation();
        };
        this.onItemClick = (evt, value) => {
            var _a, _b;
            const { onClickItem } = this.props;
            const values = this.props.values || this.state.values;
            let updateValues = ((_b = (_a = values === null || values === void 0 ? void 0 : values.asMutable) === null || _a === void 0 ? void 0 : _a.call(values)) !== null && _b !== void 0 ? _b : values) ||
                [];
            const index = updateValues.indexOf(value);
            if (index < 0) {
                updateValues.push(value);
            }
            else {
                updateValues.splice(index, 1);
            }
            // sort by label
            updateValues = updateValues.sort((v1, v2) => { var _a; return (_a = this.state.valueLabels[v1]) === null || _a === void 0 ? void 0 : _a.localeCompare(this.state.valueLabels[v2]); });
            this.setState({
                values: Immutable(updateValues)
            });
            // Only prevent default behavior for labels inside <label> to fixing the twice click problem.
            if (!['BUTTON', 'A', 'INPUT'].includes(evt.target.tagName)) {
                evt.preventDefault();
            }
            evt.stopPropagation();
            evt.nativeEvent.stopImmediatePropagation();
            onClickItem(evt, value, updateValues);
        };
        this.getValueLabel = (value) => {
            const { items } = this.props;
            const item = items.find((item) => item.value === value);
            return item ? item.label || item.value : '';
        };
        this.getShowText = () => {
            const { placeholder, displayByValues } = this.props;
            const values = this.props.values || this.state.values;
            if ((!values || (values.length === 0 && placeholder))) {
                return placeholder || '';
            }
            else {
                if (displayByValues) {
                    return displayByValues(values.asMutable());
                }
                return values.map((value) => this.getValueLabel(value)).join(',');
            }
        };
        this.getDropdownMenuStyle = () => {
            return css `
      ${this.divRef
                ? `
          min-width: ${polished.rem(this.divRef.clientWidth)};
        `
                : ''}
    `;
        };
        this.state = {
            isOpen: false,
            valueLabels: this.getValueLabels(),
            values: props.values || props.defaultValues || Immutable([])
        };
    }
    componentDidUpdate(preProps) {
        const { items, values } = this.props;
        if (preProps.items !== items) {
            this.setState({
                valueLabels: this.getValueLabels()
            });
        }
        if (preProps.values && !values) {
            this.setState({
                values: values
            });
        }
    }
    render() {
        var _a, _b;
        const _c = this.props, { items, className, size, fluid, autoWidth, appendToBody, zIndex, menuProps, menuItemProps, buttonProps } = _c, otherProps = __rest(_c, ["items", "className", "size", "fluid", "autoWidth", "appendToBody", "zIndex", "menuProps", "menuItemProps", "buttonProps"]);
        const { isOpen } = this.state;
        const isRTL = getAppStore().getState().appContext.isRTL;
        let values = this.props.values || this.state.values;
        if (!values) {
            values = Immutable([]);
        }
        const dynamicTitle = this.getShowText();
        const _d = buttonProps || {}, { showDynamicTitle, title } = _d, otherButtonProps = __rest(_d, ["showDynamicTitle", "title"]);
        const buttonTitle = showDynamicTitle ? dynamicTitle : (title || '');
        return (jsx("div", { ref: (node) => { this.divRef = node; }, className: classNames('jimu-multi-select', className || '', fluid ? 'w-100' : '') },
            jsx(Dropdown, { isOpen: isOpen, size: size, direction: 'down', toggle: this.onDropDownToggle, fluid: fluid, autoWidth: autoWidth, menuItemCheckMode: 'multiCheck' },
                jsx(DropdownButton, Object.assign({ type: 'link', size: size, "aria-label": otherProps['aria-label'], "aria-describedby": otherProps['aria-describedby'], "a11y-description": otherProps['a11y-description'] }, otherButtonProps, { title: buttonTitle }), dynamicTitle),
                jsx(DropdownMenu, Object.assign({ maxHeight: DEFAULT_MAX_HEIGHT, appendToBody: appendToBody, zIndex: zIndex, css: this.getDropdownMenuStyle(), onClick: this.handleMenuClick }, menuProps),
                    jsx("div", null, ((_b = (_a = items === null || items === void 0 ? void 0 : items.asMutable) === null || _a === void 0 ? void 0 : _a.call(items)) !== null && _b !== void 0 ? _b : items).map((item, i) => {
                        const isChecked = values.includes(item.value);
                        return (jsx(DropdownItem, Object.assign({ className: MULTI_SELECT_ITEM_CLASS_NAME, key: item.value || i, onClick: (evt) => { this.onItemClick(evt, item.value); }, toggle: false, active: isChecked }, menuItemProps),
                            jsx("div", { className: `d-flex w-100 align-items-center multi-select-item ${isChecked ? 'active' : ''}` }, item.render
                                ? (item.render(item, values))
                                : (jsx("div", { className: 'd-flex w-100 align-items-center mb-0' },
                                    jsx(Checkbox, { checked: isChecked }),
                                    jsx("div", { style: isRTL
                                            ? { marginRight: '8px' }
                                            : { marginLeft: '8px' }, className: 'item-text' }, item.label))))));
                    }))))));
    }
}
_MultiSelect.defaultProps = {
    size: 'default'
};
/**
 * The `MultiSelect` component is used to select multiple options from a `DropdownMenu`.
 *
 * ```ts
 * import { MultiSelect } from 'jimu-ui'
 * ```
 */
export const MultiSelect = withStyles(_MultiSelect, 'MultiSelect');
